import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type UserProfileResponseDto,
  type PublicBookletResponseDto,
} from "@tabipla/shared";

// Query key factory
export const userProfileKeys = {
  all: ["mekke-user-profile"] as const,
  profile: (userId: string) => [...userProfileKeys.all, "profile", userId] as const,
  booklets: (userId: string) => [...userProfileKeys.all, "booklets", userId] as const,
  bookletList: (userId: string, params: { limit?: number; offset?: number }) =>
    [...userProfileKeys.booklets(userId), params] as const,
};

// Fetch user profile
export function useUserProfile(userId: string | undefined) {
  return useQuery({
    queryKey: userProfileKeys.profile(userId ?? ""),
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      const response = await apiClient.get<UserProfileResponseDto>(
        `/api/mekke/discover/users/${userId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch user profile");
      }
      return response.data;
    },
    enabled: !!userId,
  });
}

interface UseUserBookletsOptions {
  limit?: number;
  offset?: number;
}

// Fetch user's public booklets
export function useUserBooklets(userId: string | undefined, options: UseUserBookletsOptions = {}) {
  const { limit = 20, offset = 0 } = options;

  return useQuery({
    queryKey: userProfileKeys.bookletList(userId ?? "", { limit, offset }),
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      const params = new URLSearchParams();
      if (limit) params.set("limit", limit.toString());
      if (offset) params.set("offset", offset.toString());

      const endpoint = `/api/mekke/discover/users/${userId}/booklets${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<PublicBookletResponseDto[]>(endpoint);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch user booklets");
      }
      return response.data ?? [];
    },
    enabled: !!userId,
  });
}
